import { d as debounce } from "@theme/utils";
class SearchDrawer extends window.HTMLElement {
  constructor() {
    super();
    this.searchInput = this.querySelector(".search-input");
    this.searchForm = this.querySelector(".search-form");
    this.closeButton = this.querySelector(".search-close");
    this.resultsContainer = this.querySelector(".search-results-container");
    this.productsGrid = this.querySelector(".search-products-grid");
    this.queriesContainer = this.querySelector(".search-queries");
    this.loadingState = this.querySelector(".search-loading");
    this.emptyState = this.querySelector(".search-empty");
    this.noResultsState = this.querySelector(".search-no-results");
    this.resultsCount = this.querySelector(".search-results-count");
    this.viewAllLink = this.querySelector(".search-view-all");
    this.collectionsContainer = this.querySelector(".search-collections-grid");
    this.backdrop = document.querySelector(".search-backdrop");
    this.isOpen = false;
    this.currentQuery = "";
    this.suggestions = window.search_suggestions || null;
    this.initSuggestions();
    this.init();
  }
  init() {
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.close());
    }
    if (this.backdrop) {
      this.backdrop.addEventListener("click", () => this.close());
    }
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });
    const searchIconMobile = document.getElementById("search-icon-mobile");
    if (searchIconMobile) {
      searchIconMobile.addEventListener("click", () => this.open());
    }
    if (this.searchForm) {
      this.searchForm.addEventListener("submit", (e) => {
        e.preventDefault();
        if (this.currentQuery) {
          const q = this.buildSearchQuery(this.currentQuery);
          window.location.href = `${window.routes.search_url}?q=${encodeURIComponent(q)}&type=product&options[prefix]=last&options[unavailable_products]=show`;
        }
      });
    }
    if (this.searchInput) {
      this.searchInput.addEventListener(
        "input",
        debounce((e) => {
          this.handleSearch(e.target.value);
        }, 300)
      );
    }
  }
  initSuggestions() {
    if (!this.suggestions || !this.suggestions.items?.length) return;
    const html = `
      <div class="pb-4">
        <div class="mb-3 text-lg text-center font-semibold text-secondary-dark uppercase">${this.suggestions.title}</div>
        <ul class="flex flex-col gap-2">
          ${this.suggestions.items.map(
      (text) => `<li><button type="button" data-suggestion="${text}" class="border border-gray-border flex w-full cursor-pointer items-center gap-3 rounded-lg px-4 py-3 text-left text-base text-black transition-colors hover:bg-gray-100"><svg class="h-4 w-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>${text}</button></li>`
    ).join("")}
        </ul>
      </div>
    `;
    const emptySuggestions = this.querySelector(".search-empty-suggestions");
    const noResultsSuggestions = this.querySelector(
      ".search-no-results-suggestions"
    );
    if (emptySuggestions) emptySuggestions.innerHTML = html;
    if (noResultsSuggestions) noResultsSuggestions.innerHTML = html;
    const handleClick = (e) => {
      const btn = e.target.closest("[data-suggestion]");
      if (!btn) return;
      this.searchInput.value = btn.dataset.suggestion;
      this.handleSearch(btn.dataset.suggestion);
    };
    emptySuggestions?.addEventListener("click", handleClick);
    noResultsSuggestions?.addEventListener("click", handleClick);
  }
  open() {
    this.isOpen = true;
    const stickyHeader = document.querySelector("sticky-header");
    if (stickyHeader) {
      stickyHeader.preventHide = true;
      stickyHeader.reveal();
    }
    document.body.classList.add("search-drawer-open");
    this.classList.add("active");
    if (this.backdrop) {
      this.backdrop.classList.add("active");
    }
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 100);
    this.showEmptyState();
  }
  updateHeaderHeight() {
    let header = document.querySelector("#shopify-section-header");
    if (header) {
      document.documentElement.style.setProperty(
        "--height-nav",
        `${header.offsetHeight}px`
      );
    }
  }
  close() {
    this.isOpen = false;
    this.classList.remove("active");
    document.body.classList.remove("search-drawer-open");
    const stickyHeader = document.querySelector("sticky-header");
    if (stickyHeader) {
      stickyHeader.preventHide = false;
    }
    if (this.backdrop) {
      this.backdrop.classList.remove("active");
    }
    if (this.searchInput) {
      this.searchInput.value = "";
    }
    this.currentQuery = "";
    this.showEmptyState();
  }
  async handleSearch(query) {
    this.currentQuery = query.trim();
    if (!this.currentQuery) {
      this.showEmptyState();
      return;
    }
    this.showLoadingState();
    try {
      const searchUrl = (q) => `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,query,collection&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=show&resources[options][fields]=title,product_type,tag,vendor,variants.sku`;
      const transliterated = this.transliterate(this.currentQuery);
      const fetches = [fetch(searchUrl(this.currentQuery))];
      if (transliterated !== this.currentQuery) {
        fetches.push(fetch(searchUrl(transliterated)));
      }
      const responses = await Promise.all(fetches);
      const results = await Promise.all(
        responses.map((r) => {
          if (!r.ok) throw new Error("Search request failed");
          return r.json();
        })
      );
      const data = results.length > 1 ? this.mergeResults(results[0], results[1]) : results[0];
      this.renderResults(data);
    } catch (error) {
      console.error("Search error:", error);
      this.showNoResults();
    }
  }
  renderResults(data) {
    const products = data.resources?.results?.products || [];
    const queries = data.resources?.results?.queries || [];
    const collections = data.resources?.results?.collections || [];
    if (this.viewAllLink) {
      const q = this.buildSearchQuery(this.currentQuery);
      this.viewAllLink.href = `${window.routes.search_url}?q=${encodeURIComponent(q)}&type=product&options[prefix]=last&options[unavailable_products]=show`;
    }
    if (products.length === 0 && queries.length === 0 && collections.length === 0) {
      this.showNoResults();
      return;
    }
    if (this.resultsCount) {
      this.resultsCount.textContent = products.length;
    }
    if (this.queriesContainer && queries.length > 0) {
      this.queriesContainer.innerHTML = `
        <div class="mb-4">
          <h3 class="mb-2 text-sm font-semibold text-gray-700">${document.documentElement.lang === "bg" ? "Популярни търсения" : "Popular search terms"}</h3>
          <div class="flex flex-wrap gap-2">
            ${queries.map((query) => this.renderQuerySuggestion(query)).join("")}
          </div>
        </div>
      `;
    } else if (this.queriesContainer) {
      this.queriesContainer.innerHTML = "";
    }
    if (this.productsGrid) {
      this.productsGrid.innerHTML = products.map((product) => this.renderProductCard(product)).join("");
    }
    if (this.collectionsContainer) {
      if (collections.length > 0) {
        const label = document.documentElement.lang === "bg" ? "Категории" : "Collections";
        this.collectionsContainer.innerHTML = `
          <div class="mt-4 text-xs font-semibold text-gray-400 uppercase">${label}</div>
          ${collections.map(
          (c) => `
              <a href="${c.url}" class="group flex items-center gap-4 rounded-xl border border-gray-border p-3 transition-colors hover:bg-gray-50">
                <div class="flex aspect-square w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100">
                  ${c.featured_image?.url ? `<img src="${c.featured_image.url}&width=200" alt="${c.title}" loading="lazy" class="h-full w-full object-cover">` : '<svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>'}
                </div>
                <span class="font-medium">${c.title}</span>
              </a>
            `
        ).join("")}
        `;
      } else {
        this.collectionsContainer.innerHTML = "";
      }
    }
    this.showResults();
  }
  renderQuerySuggestion(query) {
    return `
      <a
        href="${window.routes.search_url}?q=${encodeURIComponent(query.text)}&type=product&options[unavailable_products]=show"
        class="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 transition hover:bg-gray-200"
      >
        ${query.styled_text}
      </a>
    `;
  }
  parsePrice(priceStr) {
    if (typeof priceStr === "number") return priceStr;
    if (!priceStr) return 0;
    return parseFloat(
      String(priceStr).replace(/[^0-9.,]/g, "").replace(",", ".")
    ) || 0;
  }
  renderProductCard(product) {
    const priceEur = this.parsePrice(product.price_min || product.price);
    let compareHtml = "";
    const comparePrice = this.parsePrice(product.compare_at_price_min);
    if (comparePrice && comparePrice > priceEur) {
      compareHtml = `
        <span class="text-sm text-gray-400 line-through">
          €${comparePrice.toFixed(2)}
        </span>
      `;
    }
    return `
      <a href="${product.url}" class="group flex items-center gap-4 rounded-xl border border-gray-border p-3 transition-colors hover:bg-gray-50">
        <div class="aspect-square w-1/4 shrink-0 overflow-hidden rounded-lg bg-gray-100">
          ${product.image ? `<img
                src="${product.image}&width=300"
                alt="${product.title}"
                loading="lazy"
                class="h-full w-full object-cover"
              >` : '<div class="flex h-full items-center justify-center text-gray-400">No image</div>'}
        </div>
        <div class="flex-1">
          <div class="line-clamp-2 font-medium text-base">${product.title}</div>
          <div class="mt-1 flex flex-wrap items-baseline gap-2">
            <span class="font-semibold">€${priceEur.toFixed(2)}</span>
            ${compareHtml}
          </div>
        </div>
      </a>
    `;
  }
  showLoadingState() {
    this.hideAllStates();
    if (this.loadingState) {
      this.loadingState.classList.remove("hidden");
    }
  }
  showEmptyState() {
    this.hideAllStates();
    if (this.emptyState) {
      this.emptyState.classList.remove("hidden");
    }
  }
  showNoResults() {
    this.hideAllStates();
    if (this.noResultsState) {
      this.noResultsState.classList.remove("hidden");
    }
  }
  showResults() {
    this.hideAllStates();
    if (this.resultsContainer) {
      this.resultsContainer.classList.remove("hidden");
    }
  }
  hideAllStates() {
    const states = [
      this.loadingState,
      this.emptyState,
      this.noResultsState,
      this.resultsContainer
    ];
    states.forEach((state) => {
      if (state) {
        state.classList.add("hidden");
      }
    });
  }
  buildSearchQuery(query) {
    const trimmed = query.trim();
    const isCyrillic = /[а-яА-Я]/.test(trimmed);
    if (isCyrillic && trimmed.length <= 3) {
      return `${trimmed}*`;
    }
    return trimmed;
  }
  transliterate(text) {
    const lower = text.toLowerCase();
    const isCyrillic = /[а-яА-Я]/.test(text);
    if (isCyrillic) {
      const map2 = [
        ["щ", "sht"],
        ["ш", "sh"],
        ["ч", "ch"],
        ["ж", "zh"],
        ["ц", "ts"],
        ["ю", "yu"],
        ["я", "ya"],
        ["а", "a"],
        ["б", "b"],
        ["в", "v"],
        ["г", "g"],
        ["д", "d"],
        ["е", "e"],
        ["з", "z"],
        ["и", "i"],
        ["й", "y"],
        ["к", "k"],
        ["л", "l"],
        ["м", "m"],
        ["н", "n"],
        ["о", "o"],
        ["п", "p"],
        ["р", "r"],
        ["с", "s"],
        ["т", "t"],
        ["у", "u"],
        ["ф", "f"],
        ["х", "h"],
        ["ъ", "a"],
        ["ь", "y"]
      ];
      let result2 = lower;
      for (const [cyr, lat] of map2) {
        result2 = result2.replaceAll(cyr, lat);
      }
      return result2;
    }
    const map = [
      ["sht", "щ"],
      ["sh", "ш"],
      ["ch", "ч"],
      ["zh", "ж"],
      ["ts", "ц"],
      ["yu", "ю"],
      ["ya", "я"],
      ["ia", "я"],
      ["iu", "ю"],
      ["j", "ж"],
      ["4", "ч"],
      ["6", "ш"],
      ["c", "ц"],
      ["a", "а"],
      ["b", "б"],
      ["v", "в"],
      ["g", "г"],
      ["d", "д"],
      ["e", "е"],
      ["z", "з"],
      ["i", "и"],
      ["y", "й"],
      ["k", "к"],
      ["l", "л"],
      ["m", "м"],
      ["n", "н"],
      ["o", "о"],
      ["p", "п"],
      ["r", "р"],
      ["s", "с"],
      ["t", "т"],
      ["u", "у"],
      ["f", "ф"],
      ["h", "х"]
    ];
    let result = lower;
    for (const [lat, cyr] of map) {
      result = result.replaceAll(lat, cyr);
    }
    return result;
  }
  mergeResults(data1, data2) {
    const products1 = data1.resources?.results?.products || [];
    const products2 = data2.resources?.results?.products || [];
    const queries1 = data1.resources?.results?.queries || [];
    const queries2 = data2.resources?.results?.queries || [];
    const collections1 = data1.resources?.results?.collections || [];
    const collections2 = data2.resources?.results?.collections || [];
    const seen = /* @__PURE__ */ new Set();
    const mergedProducts = [];
    for (const p of [...products1, ...products2]) {
      if (!seen.has(p.id)) {
        seen.add(p.id);
        mergedProducts.push(p);
      }
    }
    const seenQueries = /* @__PURE__ */ new Set();
    const mergedQueries = [];
    for (const q of [...queries1, ...queries2]) {
      if (!seenQueries.has(q.text)) {
        seenQueries.add(q.text);
        mergedQueries.push(q);
      }
    }
    const seenCollections = /* @__PURE__ */ new Set();
    const mergedCollections = [];
    for (const c of [...collections1, ...collections2]) {
      if (!seenCollections.has(c.id)) {
        seenCollections.add(c.id);
        mergedCollections.push(c);
      }
    }
    return {
      resources: {
        results: {
          products: mergedProducts,
          queries: mergedQueries,
          collections: mergedCollections
        }
      }
    };
  }
}
window.customElements.define("search-drawer", SearchDrawer);
export {
  SearchDrawer as default
};
